import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    TextField,
    Typography
} from '@mui/material';
import {EmployeeSelect} from '../../components/employee-select/EmployeeSelect';
import {Employee, KeySnapshot, KeyTransaction, KeyVault} from '../../api/Model';
import {KeyVaultSelect} from "../../components/key-vault-select/KeyVaultSelect";
import {KeySelectInVault} from "../../components/key-select/KeySelect";
import {DateField} from "../../components/date-field/DateField";
import {useToday} from "../../util/DateUtil";
import {Page} from "../../components/page/Page";
import {executeKeyTransaction} from "../../api/KeyTransaction";
import {useFormValidation} from "../../components/form-validation/useFormValidation";
import {inputChange, mapSetState} from "../../util/FormHandlers";
import {useMessage} from "../../util/actions/Message";
import {useRemoteValue} from "../../util/RemoteValue";
import {EmployeeKeyHistory, useEmployeeKeyHistoryStore} from "../../api/EmployeeKeyHistoryStore";
import {useCurrentOfficeStore} from "../../api/CurrentOfficeStore";
import {KeyOperationDeprecationWarning} from "../../components/deprecation-warning/DeprecationWarning";

type MissingKeyReason = {
    key: string,
    text: string,
};

const missingKeyReasons = [
    {key: 'forgot', text: 'Forgot the key at home'},
    {key: 'lost', text: 'Lost the key'},
    {key: 'damaged', text: 'The existing key is not working'},
];

interface IssueKeyForm {
    keyNumber: string | undefined;
    keyVault: KeyVault;
    personType: 'employee' | 'guest';
    employee: Employee | undefined;
    guestFullName: string;
    keyReasons: { employeeKey: KeySnapshot, reason: MissingKeyReason | undefined }[];
    date: string;
    expToDate: string | undefined;
    notes: string;
}

function KeyReasonSelect({employeeKey, reason, setReason}: {
    employeeKey: KeySnapshot,
    reason?: MissingKeyReason,
    setReason: (employeeKey: KeySnapshot, reason: MissingKeyReason) => void,
}) {
    const handleReasonChange = useCallback((e: SelectChangeEvent<MissingKeyReason['key']>) => {
        const newReasonKey = e.target.value;
        const newReason = missingKeyReasons.find(reason => reason.key === newReasonKey)
        if (newReason) {
            setReason(employeeKey, newReason);
        }
    }, [employeeKey, setReason]);

    const label = `${employeeKey.key.keyNumber} (${employeeKey.historyRecord.vault.name})`;
    return (
        <Grid size={{sm: 12, md: 6}}>
            <FormControl required fullWidth>
                <InputLabel id="reason-label">{label}</InputLabel>
                <Select
                    labelId="reason-label"
                    label={label}
                    value={reason?.key || ''}
                    onChange={handleReasonChange}
                >
                    {missingKeyReasons.map(option => (
                        <MenuItem key={option.key} value={option.key}>{option.text}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
    );
}

function IssueKeyPage({defaultKeyVault}: {
    defaultKeyVault: KeyVault
}) {
    const message = useMessage();
    const [txInProgress, setTxInProgress] = useState(false);
    const employeeKeyHistoryStore = useEmployeeKeyHistoryStore();
    const employeeKeyHistory = useRemoteValue(employeeKeyHistoryStore.remoteValue, new EmployeeKeyHistory([]));
    const today = useToday();
    const officeId = useCurrentOfficeStore(state => state.officeId);

    const [form, setForm] = useState<IssueKeyForm>({
        keyNumber: undefined,
        keyVault: defaultKeyVault,
        personType: 'employee',
        employee: undefined,
        guestFullName: '',
        keyReasons: [],
        date: today,
        expToDate: undefined,
        notes: '',
    });

    const {
        formError,
        fieldErrors,
        validate: validateForm,
    } = useFormValidation<'keyNumber' | 'employee' | 'guestFullName'>();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isFormValid = validateForm({
            keyNumber: !form.keyNumber &&
                'Please enter a key number',
            employee: form.personType === 'employee' && !form.employee &&
                'Please select an employee',
            guestFullName: form.personType === 'guest' && !form.guestFullName &&
                'Please enter contractor full name',
            form: form.keyReasons.some(reason => !reason.reason) &&
                'Please select a reason for issuing another key',
        });

        if (!form.keyNumber || !isFormValid) return;

        const issueKeyTransaction: KeyTransaction = {
            keyNumber: form.keyNumber,
            vault: form.keyVault,
            date: form.date,
            newLocation: form.personType === 'employee' && form.employee ? {
                type: 'with-employee',
                employee: form.employee,
                expTo: form.expToDate,
            } : {
                type: 'with-guest',
                fullName: form.guestFullName,
                expTo: form.expToDate,
            },
            notes: form.notes,
        };

        const lostKeyTransactions: KeyTransaction[] = form.keyReasons
            .filter(keyReason => keyReason.reason?.key === 'lost')
            .map(keyReason => ({
                keyNumber: keyReason.employeeKey.key.keyNumber,
                vault: keyReason.employeeKey.historyRecord.vault,
                date: today,
                newLocation: {type: "lost"},
                notes: `Replaced by key ${form.keyNumber}`,
            }));

        const damagedKeyTransactions: KeyTransaction[] = form.keyReasons
            .filter(keyReason => keyReason.reason?.key === 'damaged')
            .map(keyReason => ({
                keyNumber: keyReason.employeeKey.key.keyNumber,
                vault: keyReason.employeeKey.historyRecord.vault,
                date: today,
                newLocation: {type: "damaged"},
                notes: `Replaced by key ${form.keyNumber}`,
            }));

        setTxInProgress(true);
        await message.exec(
            async () => {
                await executeKeyTransaction([
                    issueKeyTransaction,
                    ...lostKeyTransactions,
                    ...damagedKeyTransactions
                ])

                // Reset form
                setForm(prevForm => ({
                    ...prevForm,
                    keyNumber: undefined,
                    employee: undefined,
                    guestFullName: '',
                    notes: '',
                }))
            },
            'Key issued successfully!',
            'Failed to issue a new key. Please try again.',
        )
        setTxInProgress(false);
    };

    const handlers = useMemo(() => ({
        handlePersonTypeChange: inputChange(setForm,
            (personType: string) => ({personType: personType as 'guest' | 'employee'})
        ),
        handleGuestFullNameChange: inputChange(setForm,
            (guestFullName) => ({guestFullName})
        ),
        handleNotesChange: inputChange(setForm,
            (notes) => ({notes})
        ),
        setKeyNumber: mapSetState(setForm, 'keyNumber'),
        setKeyVault: mapSetState(setForm, 'keyVault'),
        setEmployee: mapSetState(setForm, 'employee'),
        setDate: mapSetState(setForm, 'date'),
        setExpectedReturnDate: mapSetState(setForm, 'expToDate'),
        setReason: (employeeKey: KeySnapshot, reason: MissingKeyReason) => {
            setForm(prevForm => ({
                ...prevForm,
                keyReasons: prevForm.keyReasons.map(it =>
                    it.employeeKey.key.id === employeeKey.key.id && it.employeeKey.historyRecord.id === employeeKey.historyRecord.id
                        ? {employeeKey, reason}
                        : it
                )
            }))
        }
    }), [setForm]);


    useEffect(() => {
        handlers.setKeyVault(defaultKeyVault);
    }, [officeId, handlers, defaultKeyVault]);


    const personType = form.personType;
    const employee = form.employee;
    useEffect(() => {
        setForm(prevForm => ({
            ...prevForm,
            keyReasons: personType === 'employee' && employee
                ? employeeKeyHistory.active(employee)
                    .map(employeeKey => ({employeeKey, reason: undefined}))
                : []
        }));
    }, [personType, employee, setForm, employeeKeyHistory]);

    return (
        <Page
            header="Issue Key"
            wide={false}
            description={<KeyOperationDeprecationWarning/>}
            error={formError}
        >
            <Paper elevation={3} sx={{p: 4, mt: 4}}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 8}}>
                            <FormControl fullWidth>
                                <KeySelectInVault
                                    label="Key Number"
                                    keyVault={form.keyVault}
                                    keyNumber={form.keyNumber}
                                    setKeyNumber={handlers.setKeyNumber}
                                    required
                                    {...fieldErrors.keyNumber}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={{xs: 12, md: 4}}>
                            <FormControl fullWidth required>
                                <KeyVaultSelect
                                    label="Vault"
                                    keyVault={form.keyVault}
                                    setKeyVault={handlers.setKeyVault}
                                />
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    row
                                    value={form.personType}
                                    onChange={handlers.handlePersonTypeChange}
                                >
                                    <FormControlLabel value="employee" control={<Radio/>} label="JetBrains Employee"/>
                                    <FormControlLabel value="guest" control={<Radio/>} label="Contractor"/>
                                </RadioGroup>
                            </FormControl>
                            {form.personType === 'guest' && (
                                <FormHelperText>
                                    <Alert severity="warning" sx={{mt: 1}}>
                                        Please verify with a JetBrains office manager that this contractor is authorized
                                        to receive a key
                                    </Alert>
                                </FormHelperText>
                            )}
                        </Grid>

                        {form.personType === 'employee' ? (
                            <Grid size={{xs: 12}}>
                                <FormControl fullWidth>
                                    <EmployeeSelect
                                        label="Select Employee"
                                        employee={form.employee}
                                        setEmployee={handlers.setEmployee}
                                        required
                                        {...fieldErrors.employee}
                                    />
                                </FormControl>
                            </Grid>
                        ) : (
                            <Grid size={{xs: 12}}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="Contractor Full Name"
                                        value={form.guestFullName}
                                        onChange={handlers.handleGuestFullNameChange}
                                        required
                                        {...fieldErrors.guestFullName}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {form.keyReasons.length > 0 && (
                            <Grid container spacing={2} sx={{mb: 4}} size="grow">
                                <Grid size={12}>
                                    <Typography variant="body1">{
                                        form.keyReasons.length === 1
                                            ? `Please select a reason for issuing another key (employee currently has 1 key)`
                                            : `Please select a reason for issuing another key (employee currently has ${form.keyReasons.length} keys)`
                                    }</Typography>
                                </Grid>
                                {form.keyReasons.map(keyReason => {
                                    return (
                                        <KeyReasonSelect
                                            key={`${keyReason.employeeKey.key.id}-${keyReason.employeeKey.historyRecord.id}`}
                                            reason={keyReason.reason}
                                            employeeKey={keyReason.employeeKey}
                                            setReason={handlers.setReason}
                                        />
                                    );
                                })}
                            </Grid>
                        )}

                        <Grid size={{xs: 12}}>
                            <FormControl fullWidth>
                                <DateField
                                    label="Issue Date"
                                    value={form.date}
                                    setValue={handlers.setDate}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                        },
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <FormControl fullWidth>
                                <DateField
                                    label="Expected Return Date"
                                    value={form.expToDate}
                                    setValue={handlers.setExpectedReturnDate}
                                    slotProps={{
                                        field: {
                                            clearable: true,
                                        },
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <FormControl fullWidth>
                                <TextField
                                    label="Notes"
                                    multiline
                                    rows={4}
                                    value={form.notes}
                                    onChange={handlers.handleNotesChange}
                                />
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={txInProgress}
                                fullWidth
                            >
                                {txInProgress ? 'Submitting...' : 'Issue Key'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Page>
    );
}

export default IssueKeyPage;
